import { sumKnown } from "../../utils/unknownNumbers";
import { IModifiedPosition } from "./model";

export const greeksAccessor = [
  "option.delta",
  "option.gamma",
  "option.vega",
  "option.theta",
];

export const positionValuePnlAccessor: (keyof IModifiedPosition)[] = [
  "positionValue",
  "unrealized_pnl",
  "maintenance_margin",
];

export const getColumnDataKey = (column: any) =>
  String(column?.id || column?.accessor || column?.title || "");

// Returns the summed greeks
export const computeGreeksSummary = (nestedOptionsData: any) => {
  const defaultGreeks = {
    delta: 0,
    gamma: 0,
    vega: 0,
    theta: 0,
  };

  const optionByExpiries = Object.keys(nestedOptionsData || {}).reduce(
    (acc, exp) => ({
      ...acc,
      [exp]: (nestedOptionsData || {})[exp].positions.reduce(
        (greeks, position) => {
          const d = {
            delta: greeks.delta + Number(position.option?.delta || 0),
            gamma: greeks.gamma + Number(position.option?.gamma || 0),
            vega: greeks.vega + Number(position.option?.vega || 0),
            theta: greeks.theta + Number(position.option?.theta || 0),
          };
          return d;
        },
        defaultGreeks
      ),
    }),
    {} as {
      [expiry: string]: {
        delta: number;
        gamma: number;
        vega: number;
        theta: number;
      };
    }
  );

  return {
    optionByExpiries,
    totalGreeks: Object.values(optionByExpiries).reduce(
      (prev, greeks) => ({
        delta: prev.delta + greeks.delta,
        gamma: prev.gamma + greeks.gamma,
        vega: prev.vega + greeks.vega,
        theta: prev.theta + greeks.theta,
      }),
      defaultGreeks
    ),
  };
};

/** Value / PnL / margin totals; each is `undefined` when any leg is unknown. */
export interface PositionValuePnlSummary {
  positionValue: number | undefined;
  pnl: number | undefined;
  maintenance_margin: number | undefined;
  pricePrecision: number;
}

const addKnown = (a: number | undefined, b: number | undefined) =>
  a === undefined || b === undefined ? undefined : a + b;

// Returns the summed position value, PnL and maintenance margin
export const computePositionValuePnlSummary = (nestedOptionsData: any) => {
  const defaultSummary: PositionValuePnlSummary = {
    positionValue: 0,
    pnl: 0,
    maintenance_margin: 0,
    pricePrecision: 2,
  };

  // `Number(x || 0)` counted an unknown (null) mark, PnL or margin as 0 and
  // printed a confident total that omitted that leg. An unknown leg now makes
  // its total unknown ("-").
  const summaryByExpirires = Object.keys(nestedOptionsData || {}).reduce(
    (acc, exp) => {
      const positions: any[] = (nestedOptionsData || {})[exp].positions || [];
      return {
        ...acc,
        [exp]: {
          positionValue: sumKnown(positions.map((p) => p.positionValue)),
          pnl: sumKnown(positions.map((p) => p.unrealized_pnl)),
          maintenance_margin: sumKnown(
            positions.map((p) => p.maintenance_margin)
          ),
          pricePrecision: positions.length
            ? positions[positions.length - 1].pricePrecision
            : defaultSummary.pricePrecision,
        },
      };
    },
    {} as { [expiry: string]: PositionValuePnlSummary }
  );

  return {
    optionByExpirires: summaryByExpirires,
    total: Object.values(summaryByExpirires).reduce(
      (prev, summ): PositionValuePnlSummary => ({
        ...prev,
        positionValue: addKnown(prev.positionValue, summ.positionValue),
        pnl: addKnown(prev.pnl, summ.pnl),
        maintenance_margin: addKnown(
          prev.maintenance_margin,
          summ.maintenance_margin
        ),
      }),
      defaultSummary
    ),
  };
};
